import { takeEvery, put, select, call } from "redux-saga/effects";
import { navigate } from "actions/routing";
import { trackEvent } from "actions/events";
import * as actionTypes from "constants/actionTypes";
import { EDIT_CHECKLIST_ITEM_MODAL } from "constants/globals";
import { getContentApiPostPromise } from "helpers/content-api";
import { selectAccountMeta } from "reducers/account/meta";
import { selectUserId } from "reducers/user";
import { selectAccountChecklist } from "reducers/account/checklists";
import { selectFlows } from "reducers/account/flows";

/**
 * Uses the Content API to create a checklist in the user's account.
 * As of 3/8/18, as soon as a checklist exists, the router will
 * automatically redirect to the Edit page
 */

export function* createChecklist(/*action*/) {
  const { id, uuid } = yield select(selectAccountMeta);
  const userId = yield select(selectUserId);

  try {
    const data = yield call(getContentApiPostPromise, "create", {
      account_api_key: uuid,
      account_id: id,
      content_type: "checklist",
      user_id: userId
    });
    yield put(
      trackEvent("Created a checklist", {
        checklist_id: data.new_content_id
      })
    );
    yield put(navigate(`/checklists/${data.new_content_id}/edit`));
  } catch (e) {
    console.error(`Failed to create checklist`, e.toString());
  }
}

export function* cloneChecklist(action) {
  const { id, uuid } = yield select(selectAccountMeta);
  const userId = yield select(selectUserId);
  const checklistId = action.payload.checklistId;

  try {
    const data = yield call(getContentApiPostPromise, "clone", {
      account_api_key: uuid,
      account_id: id,
      content_type: "checklists",
      user_id: userId,
      content_id: checklistId
    });
    yield put(
      trackEvent("cloned a checklist", {
        checklist_id: data.new_content_id
      })
    );
    yield put(navigate(`/checklists/${data.new_content_id}/edit`));
  } catch (e) {
    console.error(`Failed to create checklist`, e.toString());
  }
}

export function* updateChecklist(action) {
  const { checklistId, changes } = action.payload;

  yield put({
    type: actionTypes.UPDATE_CHECKLIST_IN_STORE,
    payload: {
      checklistId,
      changes
    }
  });

  yield put({
    type: actionTypes.FLUSH_CHECKLIST_TO_FIREBASE,
    payload: {
      checklistId
    }
  });

  yield put(
    trackEvent("Updated a checklist", {
      checklist_id: checklistId
    })
  );
}

export function* updateChecklistItemAndFlushChecklist(action) {
  const {
    payload: { checklistId, checklistItemId, changes }
  } = action;
  yield put({
    type: actionTypes.UPDATE_CHECKLIST_ITEM_IN_STORE,
    payload: {
      checklistId,
      checklistItemId,
      changes
    }
  });

  yield put({
    type: actionTypes.FLUSH_CHECKLIST_TO_FIREBASE,
    payload: {
      checklistId
    }
  });

  yield put(
    trackEvent("Updated a checklist item", {
      checklist_id: checklistId,
      checklist_item_id: checklistItemId
    })
  );
}

export function* addItemToChecklist(action) {
  const {
    payload: { checklistId, shouldOpenModal }
  } = action;

  const { id, uuid } = yield select(selectAccountMeta);

  yield put({
    type: actionTypes.ADD_CHECKLIST_ITEM_FETCHING
  });

  try {
    const data = yield call(getContentApiPostPromise, "create", {
      account_api_key: uuid,
      account_id: id,
      content_type: "checklist-item",
      parent_id: checklistId
    });
    const checklistItemId = data.new_content_id;
    yield put(
      trackEvent("Added a checklist item", {
        checklist_id: checklistId,
        checklist_item_id: checklistItemId
      })
    );

    const checklist = yield select(selectAccountChecklist, checklistId);
    const checklistItems = checklist.items;

    yield put({
      type: actionTypes.CALCULATE_CHECKLIST_ITEM_DEPENDENCIES,
      payload: { checklistId, checklistItems }
    });

    if (shouldOpenModal) {
      const steps = yield select(selectFlows);

      yield put({
        type: actionTypes.SHOW_MODAL,
        payload: {
          key: EDIT_CHECKLIST_ITEM_MODAL,
          props: {
            checklistId,
            checklistItemId,
            steps
          }
        }
      });
    }
  } catch (e) {
    console.error(`Failed to add item to checklist ${id}`);
  }
  yield put({
    type: actionTypes.ADD_CHECKLIST_ITEM_FETCHED
  });
}

export function* removeItemFromChecklist(action) {
  const {
    payload: { checklistId, checklistItemId, checklistItems }
  } = action;

  const { id, uuid } = yield select(selectAccountMeta);

  try {
    const removedItemIndex = checklistItems[checklistItemId].index;
    const checklistItemContent = Object.values(checklistItems || {});
    const isLastItem = removedItemIndex === checklistItemContent.length - 1;

    if (!isLastItem) {
      const itemCopy = { ...checklistItems };

      const itemsToUpdate = checklistItemContent.filter(
        item => item.index > removedItemIndex
      );

      const filteredItems = checklistItemContent.filter(
        item => item.id !== checklistItemId
      );

      itemsToUpdate.forEach(item => {
        itemCopy[item.id].index--;
      });

      delete itemCopy[checklistItemId];

      yield put({
        type: actionTypes.UPDATE_CHECKLIST_IN_STORE,
        payload: {
          checklistId,
          changes: { items: itemCopy }
        }
      });

      yield call(setChecklistItemDependencies, checklistId, filteredItems);

      yield put({
        type: actionTypes.FLUSH_CHECKLIST_TO_FIREBASE,
        payload: {
          checklistId
        }
      });
    } else {
      yield call(getContentApiPostPromise, "delete", {
        account_api_key: uuid,
        account_id: id,
        content_type: "checklist-item",
        content_id: checklistItemId,
        parent_content_type: "checklist",
        parent_id: checklistId
      });
    }

    yield put(
      trackEvent("Removed a checklist item", {
        checklist_id: checklistId,
        checklist_item_id: checklistItemId
      })
    );
  } catch (e) {
    console.error(`Failed to delete checklist ${checklistId}`);
  }
}

export function* calculateChecklistItemDependencies(action) {
  const {
    payload: { checklistId, checklistItems }
  } = action;
  yield call(setChecklistItemDependencies, checklistId, checklistItems);
  yield put({
    type: actionTypes.FLUSH_CHECKLIST_TO_FIREBASE,
    payload: {
      checklistId
    }
  });
}

export function* reorderChecklistItems(action) {
  const {
    payload: { itemId1, newIndex1, itemId2, newIndex2, checklistId }
  } = action;
  // update item indices (a.index <-> b.index)
  yield call(
    updateChecklistItemIndices,
    itemId1,
    newIndex1,
    itemId2,
    newIndex2,
    checklistId
  );
  // recalculate checklist item dependencies
  const checklist = yield select(selectAccountChecklist, checklistId);
  yield call(setChecklistItemDependencies, checklistId, checklist.items);
  // save final checklist to firebase
  yield put({
    type: actionTypes.FLUSH_CHECKLIST_TO_FIREBASE,
    payload: {
      checklistId
    }
  });
}

export function* updateChecklistItemIndices(
  itemId1,
  newIndex1,
  itemId2,
  newIndex2,
  checklistId
) {
  yield put({
    type: actionTypes.UPDATE_CHECKLIST_ITEM_IN_STORE,
    payload: {
      checklistId,
      checklistItemId: itemId1,
      changes: { index: newIndex1 }
    }
  });
  yield put({
    type: actionTypes.UPDATE_CHECKLIST_ITEM_IN_STORE,
    payload: {
      checklistId,
      checklistItemId: itemId2,
      changes: { index: newIndex2 }
    }
  });
}

export function* setChecklistItemDependencies(checklistId, checklistItems) {
  const orderedChecklistItems = Object.values(checklistItems).sort(
    (a, b) => a.index - b.index
  );
  for (let i = 0; i < orderedChecklistItems.length; i++) {
    if (i === 0) {
      // set dependency to null if item is index 0
      yield put({
        type: actionTypes.UPDATE_CHECKLIST_ITEM_IN_STORE,
        payload: {
          checklistId,
          checklistItemId: orderedChecklistItems[i].id,
          changes: { dependsOn: [] }
        }
      });
    } else {
      yield put({
        type: actionTypes.UPDATE_CHECKLIST_ITEM_IN_STORE,
        payload: {
          checklistId,
          checklistItemId: orderedChecklistItems[i].id,
          changes: { dependsOn: [orderedChecklistItems[i - 1].id] }
        }
      });
    }
  }
}

export function* deleteChecklist(action) {
  const {
    payload: { checklistId }
  } = action;

  const { id, uuid } = yield select(selectAccountMeta);

  try {
    yield call(getContentApiPostPromise, "delete", {
      account_api_key: uuid,
      account_id: id,
      content_type: "checklist",
      content_id: checklistId
    });

    yield put(
      trackEvent("Deleted a checklist", {
        checklist_id: checklistId
      })
    );
  } catch (e) {
    console.error(`Failed to delete checklist ${id}`);
  }
}

export function* checklists() {
  yield takeEvery(actionTypes.CREATE_CHECKLIST, createChecklist);
  yield takeEvery(actionTypes.UPDATE_CHECKLIST, updateChecklist);
  yield takeEvery(
    actionTypes.UPDATE_CHECKLIST_ITEM_AND_FLUSH_CHECKLIST,
    updateChecklistItemAndFlushChecklist
  );
  yield takeEvery(actionTypes.ADD_ITEM_TO_CHECKLIST, addItemToChecklist);
  yield takeEvery(
    actionTypes.REMOVE_ITEM_FROM_CHECKLIST,
    removeItemFromChecklist
  );
  yield takeEvery(actionTypes.DELETE_CHECKLIST, deleteChecklist);
  yield takeEvery(actionTypes.REORDER_CHECKLIST_ITEMS, reorderChecklistItems);
  yield takeEvery(
    actionTypes.CALCULATE_CHECKLIST_ITEM_DEPENDENCIES,
    calculateChecklistItemDependencies
  );
  yield takeEvery(actionTypes.CLONE_CHECKLIST, cloneChecklist);
}

export default checklists;
